import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FileText, Layers, DollarSign } from 'lucide-react';

const money = (amount, currency = 'USD') => `${currency} ${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const paidAmount = (invoice) => (invoice.installments || []).length
  ? invoice.installments.filter(i => i.status === 'paid').reduce((s, i) => s + Number(i.amount || 0), 0)
  : invoice.status === 'paid' ? Number(invoice.total || 0) : 0;

const ClientContract = () => {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/api/clients/me/profile')
      .then(r => setClient(r.data.data))
      .catch(async () => {
        const projects = await axios.get('/api/projects');
        const first = projects.data.data?.[0]?.clientId;
        if (first) setClient((await axios.get(`/api/clients/${first}`)).data.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const totals = useMemo(() => {
    const invoices = client?.invoices || [];
    return invoices.reduce((acc, inv) => {
      acc.total += Number(inv.total || 0);
      acc.paid += paidAmount(inv);
      acc.currency = inv.currency || acc.currency;
      return acc;
    }, { total: 0, paid: 0, currency: 'USD' });
  }, [client]);

  if (loading) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Loading...</div>;
  if (!client) return <div className="page-content"><div className="empty-state">No contract data yet</div></div>;
  const remaining = Math.max(0, totals.total - totals.paid);

  return (
    <div className="page-content">
      <div className="flex-between mb-24">
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800 }}>Services & Contract</h1>
          <p style={{ color: 'var(--text-muted)' }}>{client.companyName}</p>
        </div>
      </div>
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-icon"><Layers /></div><div className="stat-value">{client.clientServices?.length || 0}</div><div className="stat-label">Active Services</div></div>
        <div className="stat-card"><div className="stat-icon"><DollarSign /></div><div className="stat-value" style={{ fontSize: 20 }}>{money(totals.total, totals.currency)}</div><div className="stat-label">Contract Total</div></div>
        <div className="stat-card"><div className="stat-icon"><FileText /></div><div className="stat-value" style={{ fontSize: 20 }}>{money(totals.paid, totals.currency)}</div><div className="stat-label">Paid</div></div>
        <div className="stat-card"><div className="stat-icon"><DollarSign /></div><div className="stat-value" style={{ fontSize: 20 }}>{money(remaining, totals.currency)}</div><div className="stat-label">Remaining</div></div>
      </div>
      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table><thead><tr><th>Service</th><th>Details</th><th>Status</th><th>Qty</th><th>Price</th><th>Total</th></tr></thead><tbody>
            {(client.clientServices || []).map(s => <tr key={s.id}><td style={{ fontWeight: 700 }}>{s.name}</td><td>{s.description || '-'}</td><td><span className="badge badge-purple">{s.status}</span></td><td>{Number(s.quantity || 1)}</td><td>{money(s.unitPrice, s.currency)}</td><td>{money(Number(s.quantity || 1) * Number(s.unitPrice || 0), s.currency)}</td></tr>)}
            {!client.clientServices?.length && <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>No services yet</td></tr>}
          </tbody></table>
        </div>
      </div>
    </div>
  );
};

export default ClientContract;
